import { CircleCheck, CircleMinus, Settings } from 'lucide-react'
import { useMatch } from 'react-router'
import { H4 } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { ChangeDefaultRoleSheet } from '@/routes/single-sign-on/jit/change-default-role-sheet'
import { ToggleJitDialog } from '@/routes/single-sign-on/jit/toggle-jit-dialog'
import type {
  JitSsoDetailsQuery,
  JitSsoDetailsQueryVariables,
} from '@/routes/single-sign-on/jit/index.types.gen'

const JIT_DETAILS_QUERY = gql`
  query JitSsoDetails($idCompany: String!) {
    company(id: $idCompany) {
      id
      samlSetting @live {
        id
        isJitEnabled
        jitDefaultRoles @live {
          id
          idRole
          type
          idSite
        }
      }
      draftSamlSetting: samlSetting(isDraft: true) @live {
        id
        isJitEnabled
        jitDefaultRoles @live {
          id
          idRole
          type
          idSite
        }
      }
    }
  }
`

const getRoleName = (roleId: string | null | undefined): string => {
  const roleMap: Record<string, string> = {
    'company-developer': 'Company developer',
    'company-billing': 'Company billing',
    'company-admin': 'Company admin',
    'company-owner': 'Company owner',
  }
  return roleMap[roleId ?? ''] ?? 'Company developer'
}

export const Jit = () => {
  const { idCompany } = useIdCompany()
  const { navigateToPath } = useCompanyPath()
  const isToggleDialogOpen = Boolean(useMatch('/single-sign-on/toggle-jit'))
  const isRoleSheetOpen = Boolean(
    useMatch('/single-sign-on/change-default-role')
  )

  const { data } = useSuspenseQuery<
    JitSsoDetailsQuery,
    JitSsoDetailsQueryVariables
  >(JIT_DETAILS_QUERY, {
    variables: { idCompany },
  })

  const liveSamlSetting = data?.company?.samlSetting
  const draftSamlSetting = data?.company?.draftSamlSetting
  const isEnabled =
    liveSamlSetting?.isJitEnabled ?? draftSamlSetting?.isJitEnabled ?? false
  const defaultRoles =
    liveSamlSetting?.jitDefaultRoles ?? draftSamlSetting?.jitDefaultRoles ?? []

  const companyLevelDefaultRole = defaultRoles.find(
    (role) => role.type === 'company'
  )

  const serviceLevelRoles = defaultRoles.filter(
    (role) => role.type === 'site' && role.idSite
  )

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            <H4>Just-in-time provisioning</H4>
          </CardTitle>
          <CardAction>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  navigateToPath({
                    pathname: '/single-sign-on/change-default-role',
                    options: { replace: true },
                  })
                }
              >
                <Settings aria-hidden="true" />
                Change default role
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  navigateToPath({
                    pathname: '/single-sign-on/toggle-jit',
                    options: { replace: true },
                  })
                }
              >
                {isEnabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </CardAction>
          <CardDescription>
            JIT enables anyone allowed by the identity provider's settings to
            join this company account. Typically, that means anyone who has an
            email address with a specified domain.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {isEnabled ? (
              <CircleCheck className="size-4 fill-emerald-600 stroke-background flex-shrink-0" />
            ) : (
              <CircleMinus className="size-4 stroke-background fill-muted-foreground flex-shrink-0" />
            )}
            <span
              className={cn(
                isEnabled
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-muted-foreground'
              )}
            >
              {isEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <span className="text-muted-foreground">•</span>
            <span
              className={cn(
                isEnabled
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-muted-foreground'
              )}
            >
              Default role:
            </span>
            {companyLevelDefaultRole ? (
              <span
                className={cn(
                  isEnabled
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-muted-foreground'
                )}
              >
                {getRoleName(companyLevelDefaultRole.idRole)}
              </span>
            ) : defaultRoles.length > 0 ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      'cursor-help border-b border-dashed',
                      isEnabled
                        ? 'text-green-600 dark:text-green-400 border-green-400'
                        : 'text-muted-foreground border-muted-foreground'
                    )}
                  >
                    Service level ({serviceLevelRoles.length}{' '}
                    {serviceLevelRoles.length === 1 ? 'service' : 'services'})
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <ul className="text-xs">
                    {serviceLevelRoles.map((role) => (
                      <li key={role.id}>
                        {role.idSite} • {getRoleName(role.idRole)}
                      </li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            ) : (
              <span
                className={cn(
                  isEnabled
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-muted-foreground'
                )}
              >
                Company developer
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <ToggleJitDialog
        isOpen={isToggleDialogOpen}
        onClose={() =>
          navigateToPath({
            pathname: '/single-sign-on',
            options: { replace: true },
          })
        }
      />
      <ChangeDefaultRoleSheet
        isOpen={isRoleSheetOpen}
        onClose={() =>
          navigateToPath({
            pathname: '/single-sign-on',
            options: { replace: true },
          })
        }
      />
    </>
  )
}
